/* eslint @typescript-eslint/no-explicit-any: 0 */
import { Auth } from '@aws-amplify/auth';
import { setUser } from '@sentry/nextjs';

import {
  analysisUserPropertyKeys,
  resetAnalysisUser,
  setAnalysisUserProperty,
  //eslint-disable-next-line import/no-restricted-paths
} from 'utils/analysis/analysis';
import {
  analysisEventKey,
  trackAnalysisEvent,
  //eslint-disable-next-line import/no-restricted-paths
} from 'utils/analysis/event';

import type { CognitoUser } from '@aws-amplify/auth';

import { awsmobile } from './configure';

Auth.configure(awsmobile.Auth);

class InspectorAuthInteractor {
  /**
   * Retrieve current authenticated user.
   * See https://docs.amplify.aws/lib/auth/manageusers/q/platform/js/#retrieve-current-authenticated-user .
   *
   * @returns `Promise<CognitoUser | null>`
   * `CognitoUser` if logged in, else `null`
   * */
  private async getCurrentUser(): Promise<CognitoUser> {
    const currentUser = (await Auth.currentAuthenticatedUser({
      bypassCache: false,
    })) as CognitoUser;
    if (currentUser !== null) {
      setAnalysisUserProperty(
        analysisUserPropertyKeys.COGNITO_USERNAME,
        currentUser.getUsername(),
      );
      setUser({ username: currentUser.getUsername() });
    }
    return currentUser;
  }

  public async isSignIn(): Promise<boolean> {
    const currentUser = await this.getCurrentUser().catch(() => null);
    return currentUser != null;
  }

  public async signIn(email: string, password: string): Promise<void> {
    try {
      await Auth.signIn(email, password);
      trackAnalysisEvent(analysisEventKey.SIGNED_IN, { email });
    } catch (err: unknown) {
      if (err instanceof Error) {
        throw err;
      }
    }
  }

  public async signOut(): Promise<void> {
    await Auth.signOut();
    trackAnalysisEvent(analysisEventKey.SIGNED_OUT, {});
    resetAnalysisUser();
  }

  public async getAuthToken() {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  }
}

export const inspectorAuthInteractor = new InspectorAuthInteractor();
